import { type FC, type ReactNode } from "react";
import type { TFunction } from "i18next";
import {
  CHART_AXIS_STROKE,
  CHART_GRID_STROKE,
  CHART_TICK_COLOR,
  CHART_TOOLTIP_CURSOR,
} from "src/components/charts/chartPalette";
import { mirrorChartMargin, rtlChartProps } from "src/components/charts/chartRtl";
import { DefinitionTooltip, type DefinitionTooltipInfo } from "src/components/common";
import { toArabicIndicDigitsInString, wrapBidiIsolate } from "src/utils/numberFormat";

export { mirrorChartMargin, rtlChartProps };

export const localizeChartDigits = (value: string, isRTL: boolean) =>
  isRTL ? wrapBidiIsolate(toArabicIndicDigitsInString(value)) : value;

export const panelTooltip = (
  t: TFunction,
  key: string,
  descriptionDefault: string,
  extra?: { formulaDefault?: string; noteDefault?: string }
): DefinitionTooltipInfo => ({
  description: t(`salesAttribution.tooltips.panels.${key}.description`, {
    defaultValue: descriptionDefault,
  }),
  ...(extra?.formulaDefault
    ? {
        formula: t(`salesAttribution.tooltips.panels.${key}.formula`, {
          defaultValue: extra.formulaDefault,
        }),
      }
    : {}),
  ...(extra?.noteDefault
    ? {
        note: t(`salesAttribution.tooltips.panels.${key}.note`, {
          defaultValue: extra.noteDefault,
        }),
      }
    : {}),
});

export const SOURCE_COLORS = {
  shopify: "#2f9e63",
  ga4: "#e0a92b",
  googleAds: "#2b74e4",
  metaAds: "#d1495b",
} as const;

export type SalesAttributionSourceColor = keyof typeof SOURCE_COLORS;

export const CHART_CHROME = {
  grid: CHART_GRID_STROKE,
  axis: CHART_AXIS_STROKE,
  tick: CHART_TICK_COLOR,
  cursor: CHART_TOOLTIP_CURSOR,
} as const;

export const AXIS_TICK = { fill: CHART_TICK_COLOR, fontSize: 11 } as const;

export const shadeColor = (hex: string, amount: number): string => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map(char => char + char)
          .join("")
      : normalized;
  const value = Number.parseInt(expanded, 16);
  const mix = (channel: number) =>
    Math.round(amount >= 0 ? channel + (255 - channel) * amount : channel * (1 + amount))
      .toString(16)
      .padStart(2, "0");

  return `#${mix((value >> 16) & 255)}${mix((value >> 8) & 255)}${mix(value & 255)}`;
};

interface SalesAttributionPanelProps {
  title: string;
  definition: DefinitionTooltipInfo;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Card shell used by every panel on the dashboard. */
export const SalesAttributionPanel: FC<SalesAttributionPanelProps> = ({
  title,
  definition,
  subtitle,
  actions,
  children,
  className = "",
}) => (
  <section className={`sales-attribution__panel ${className}`.trim()}>
    <header className="sales-attribution__panel-header">
      <div className="sales-attribution__panel-heading">
        <div className="sales-attribution__panel-title-row">
          <h3 className="sales-attribution__panel-title">{title}</h3>
          <DefinitionTooltip label={title} info={definition} />
        </div>
        {subtitle ? (
          <p className="sales-attribution__panel-subtitle">{subtitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="sales-attribution__panel-actions">{actions}</div>
      ) : null}
    </header>
    <div className="sales-attribution__panel-body">{children}</div>
  </section>
);

export const LEGEND_PROPS = {
  iconType: "circle",
  iconSize: 8,
  wrapperStyle: { fontSize: 11, paddingTop: 8 },
} as const;

